/**
 * Chapter chunks and chunk explanations CRUD operations.
 * These stores are shared across users (not user-scoped).
 */

import { ensureDB, getDB, getById, put, remove, now } from './helpers';

// =====================================================
// Chapter Chunks (LLM-generated chunk annotations)
// =====================================================

export async function getChapterChunks(chapterContentHash) {
  return getById('chapter_chunks', chapterContentHash);
}

export async function putChapterChunks(data) {
  return put('chapter_chunks', data);
}

export async function deleteChapterChunks(chapterContentHash) {
  return remove('chapter_chunks', chapterContentHash);
}

/**
 * Get all locally-cached chapter_chunks records for a canonical book.
 * Uses the canonical_book_id index ([helpers.js:162]).
 * Used by reconcileBookChunks to find records that may have been upgraded server-side.
 */
export async function getChapterChunksByBookId(canonicalBookId) {
  if (!(await ensureDB()) || !canonicalBookId) return [];
  const db = getDB();
  if (!db.objectStoreNames.contains('chapter_chunks')) return [];
  return new Promise((resolve, reject) => {
    const tx = db.transaction('chapter_chunks', 'readonly');
    const idx = tx.objectStore('chapter_chunks').index('canonical_book_id');
    const req = idx.getAll(canonicalBookId);
    req.onsuccess = () => resolve(req.result || []);
    req.onerror = () => reject(req.error);
  });
}

/**
 * Batch query: get chunks for multiple chapter hashes at once.
 * Returns a Map of hash → chunks record (only for hashes that exist locally).
 */
export async function getChapterChunksBatch(hashes) {
  if (!(await ensureDB()) || !hashes.length) return new Map();
  const db = getDB();
  if (!db.objectStoreNames.contains('chapter_chunks')) return new Map();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('chapter_chunks', 'readonly');
    const store = tx.objectStore('chapter_chunks');
    const results = new Map();
    let pending = hashes.length;

    for (const hash of hashes) {
      const request = store.get(hash);
      request.onsuccess = () => {
        if (request.result) results.set(hash, request.result);
        if (--pending === 0) resolve(results);
      };
      request.onerror = () => {
        if (--pending === 0) resolve(results);
      };
    }

  });
}

// =====================================================
// Chunk Explanations (Lens LLM explanations cache)
// =====================================================

export async function getChunkExplanation(id) {
  return getById('chunk_explanations', id);
}

export async function putChunkExplanation(data) {
  return put('chunk_explanations', { ...data, created_at: data.created_at || now() });
}

/**
 * Look up chunk explanation with three-level fallback:
 * 1. Exact match (same chapter)
 * 2. Same book (any chapter)
 * 3. Any book (generic)
 */
/**
 * Delete all chunk explanations matching a lookup key (text + source + target language).
 * Used when a user reports a bad cached translation.
 */
export async function deleteChunkExplanationsByLookup(chunkText, sourceLang, targetLang) {
  if (!(await ensureDB())) return;
  const db = getDB();
  if (!db.objectStoreNames.contains('chunk_explanations')) return;
  return new Promise((resolve, reject) => {
    const tx = db.transaction('chunk_explanations', 'readwrite');
    const store = tx.objectStore('chunk_explanations');
    const index = store.index('lookup');
    const request = index.openCursor([chunkText, sourceLang, targetLang]);
    request.onsuccess = (e) => {
      const cursor = e.target.result;
      if (cursor) {
        cursor.delete();
        cursor.continue();
      }
    };
    tx.oncomplete = () => resolve();
    tx.onerror = () => reject(tx.error);
  });
}

export async function findChunkExplanation(chunkText, sourceLang, targetLang, chapterHash, canonicalBookId) {
  if (!(await ensureDB())) return null;
  const db = getDB();
  if (!db.objectStoreNames.contains('chunk_explanations')) return null;
  return new Promise((resolve, reject) => {
    const tx = db.transaction('chunk_explanations', 'readonly');
    const store = tx.objectStore('chunk_explanations');
    const index = store.index('lookup');
    const request = index.getAll([chunkText, sourceLang, targetLang]);

    request.onsuccess = () => {
      const results = request.result || [];
      // Priority: exact chapter > same book > generic
      const exact = results.find(r => r.chapter_content_hash === chapterHash);
      if (exact) return resolve(exact);
      const sameBook = results.find(r => r.canonical_book_id === canonicalBookId);
      if (sameBook) return resolve(sameBook);
      resolve(results[0] || null);
    };
    request.onerror = () => reject(request.error);
  });
}
